import React, { createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import type { OrderState, PaymentMethod } from '../shared/types';
import { reducer, Action } from '../shared/orderReducer';
import { fetchState, postAction, RemoteState, UnauthorizedError } from '../data/apiClient';
import { useAuth } from './AuthContext';

const POLL_INTERVAL_MS = 2500;

type SetRemoteAction = { type: '__SET_REMOTE'; remote: RemoteState };

const initialState: RemoteState = {
  tables: {},
  unavailableItemIds: {},
  version: 0,
  updatedAt: 0,
};

function stateReducer(state: RemoteState, action: Action | SetRemoteAction): RemoteState {
  if (action.type === '__SET_REMOTE') {
    return action.remote;
  }
  // Optimistic local update — version/updatedAt are only ever set from the
  // server response (via __SET_REMOTE), never guessed locally.
  const next = reducer(state, action);
  return { ...state, ...next };
}

interface OrderContextValue {
  state: OrderState;
  addItem: (table: number, itemId: string) => void;
  changeQty: (table: number, itemId: string, delta: number) => void;
  sendToKitchen: (table: number) => void;
  markDelivered: (table: number) => void;
  payTable: (table: number, method: PaymentMethod) => void;
  toggleItemAvailability: (itemId: string) => void;
}

const OrderContext = createContext<OrderContextValue | null>(null);

export const OrderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const versionRef = useRef(state.version);
  versionRef.current = state.version;
  const { logout } = useAuth();

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const remote = await fetchState();
        if (!cancelled && remote.version > versionRef.current) {
          dispatch({ type: '__SET_REMOTE', remote });
        }
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          logout();
        }
        // Otherwise a network hiccup — the next poll tick will retry.
      }
    }

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatchAction = (action: Action) => {
    dispatch(action);
    postAction(action)
      .then((remote) => dispatch({ type: '__SET_REMOTE', remote }))
      .catch(async (err) => {
        if (err instanceof UnauthorizedError) {
          logout();
          return;
        }
        // The write didn't land — reconcile with whatever the server
        // actually has rather than leaving a stuck optimistic guess.
        try {
          const remote = await fetchState();
          dispatch({ type: '__SET_REMOTE', remote });
        } catch (err2) {
          if (err2 instanceof UnauthorizedError) {
            logout();
          }
          // Still offline — a later poll tick will retry.
        }
      });
  };

  const value = useMemo<OrderContextValue>(
    () => ({
      state,
      addItem: (table, itemId) => dispatchAction({ type: 'ADD_ITEM', table, itemId }),
      changeQty: (table, itemId, delta) =>
        dispatchAction({ type: 'CHANGE_QTY', table, itemId, delta }),
      sendToKitchen: (table) =>
        dispatchAction({ type: 'SEND_TO_KITCHEN', table, timestamp: Date.now() }),
      markDelivered: (table) => dispatchAction({ type: 'MARK_DELIVERED', table }),
      payTable: (table, method) => dispatchAction({ type: 'PAY_TABLE', table, method }),
      toggleItemAvailability: (itemId) =>
        dispatchAction({ type: 'TOGGLE_ITEM_AVAILABILITY', itemId }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
};

export function useOrder(): OrderContextValue {
  const ctx = useContext(OrderContext);
  if (!ctx) throw new Error('useOrder must be used within an OrderProvider');
  return ctx;
}
